import { useEffect, useState } from 'react';
import {
  AuthenticatedTemplate,
  MsalProvider as BaseMsalProvider,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { initializeMsal, msalInstance } from '@/config/msal.instance';
import { useAuthStore } from '@/stores';
import {
  acquireMsalAccessToken,
  isTokenExpiringSoon,
} from '@/services/api/tokenUtils';
import { TokenStorage } from '@/services/auth/tokenStorage';
import { TokenService } from '@/services/auth/tokenService';

/**
 * Component that wraps the app with MSAL authentication
 */
export function MsalProvider({ children }: { children: React.ReactNode }) {
  const { setMsalAuthentication, clearMsalAuthentication } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize MSAL before using it
    initializeMsal().then(() => {
      setIsInitialized(true);
    });
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    // Register event callbacks
    const callbackId = msalInstance.addEventCallback(event => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        // Login success - logging disabled to comply with linting rules
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        // Logout success - logging disabled to comply with linting rules
        clearMsalAuthentication();
      }

      if (
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
        event.payload
      ) {
        // Token acquired - logging disabled to comply with linting rules
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        console.error('MSAL: Token acquisition failed', event.payload);
      }
    });

    // Set active account if one exists
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      if (account) {
        msalInstance.setActiveAccount(account);
        setMsalAuthentication(account);
      }
    }

    // Cleanup
    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [isInitialized, setMsalAuthentication, clearMsalAuthentication]);

  // Proactive token refresh - check every minute if token is expiring soon
  // Also refresh when tab becomes visible again (setInterval stops when tab is suspended)
  useEffect(() => {
    if (!isInitialized) return;

    const refreshTokensIfNeeded = () => {
      const account = msalInstance.getActiveAccount();
      if (!account) return;

      // Refresh MSAL token if expiring soon
      if (isTokenExpiringSoon()) {
        acquireMsalAccessToken().catch(error => {
          console.warn('Proactive MSAL token refresh failed:', error);
        });
      }

      // Refresh MPN token if expiring soon
      if (TokenStorage.isAccessTokenExpiring()) {
        TokenService.refreshIfNeeded().catch(error => {
          console.warn('Proactive MPN token refresh failed:', error);
        });
      }
    };

    // Check on interval (runs while tab is active)
    const refreshInterval = setInterval(refreshTokensIfNeeded, 60 * 1000);

    // Also check when tab becomes visible again after being suspended/hidden
    // This covers the case where the user was idle and the browser suspended the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshTokensIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitialized]);

  // Wait for MSAL to initialize before rendering
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <BaseMsalProvider instance={msalInstance}>{children}</BaseMsalProvider>
  );
}

/**
 * Export templates for conditional rendering based on auth state
 */
export { AuthenticatedTemplate, UnauthenticatedTemplate };
